"use client";

import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  PlaneLanding,
  PlaneTakeoff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDismissedChecks } from "@/hooks/useDismissedChecks";
import {
  createEvidenceLabelResolver,
  type EvidenceLabelResolver,
} from "@/lib/aicheck/evidence-labeler";
import {
  collectEvidenceEntries,
  type EvidenceTraceEntry,
} from "@/lib/aicheck/evidence-traceability";
import { useAICheckStore } from "@/stores/aicheck";
import type {
  AICheckRunResult,
  BasicRequestsSummaryResult,
  CheckCategory,
  ContactTopic,
  ContactTopicsResult,
  EvidenceSource,
  ExtractedNote,
  FocusedEvidenceTarget,
  TraceabilityHistoryState,
} from "@/types/aicheck";
import { sanitizeEvidenceRefs } from "@/types/aicheck";
import { RunDiffSummaryBadges } from "./RunDiffSummaryBadges";
import { CheckItemCard } from "./shared/CheckItemCard";
import {
  CheckItemDetailEmpty,
  CheckItemDetailPanel,
} from "./shared/CheckItemDetailPanel";
import { CitationPopover } from "./shared/CitationPopover";
import { DialogShell } from "./shared/DialogShell";
import { MarkdownText } from "./shared/MarkdownText";
import type { CheckSource } from "./shared/SourceBadge";
import { SourceBadge } from "./shared/SourceBadge";
import { StatusFilterButtons } from "./shared/StatusFilterButtons";
import {
  ActionDecisionButtons,
  EvidenceValue,
} from "./shared/StructuredResultView";
import { SummaryWithCitations } from "./shared/SummaryWithCitations";
import { scrollElementIntoContainer } from "./shared/scroll-utils";
import { compareByStatusAndImportance } from "./shared/sort-utils";
import { getImportanceColor } from "./shared/style-utils";

type BasicRequestsTab = "basic" | "review" | "reminders";

interface BasicRequestsViewProps {
  result: AICheckRunResult;
  showExecutiveSummary?: boolean;
  onJumpToTab?: (tab: BasicRequestsTab) => void;
  onTraceabilityHistoryPush?: (state: TraceabilityHistoryState) => void;
  onReturnToSummary?: (findingIndex: number) => void;
  onProjectDataReferenceClick?: (source: string) => void;
  /** U6: 「解消/新規/継続」バッジ帯クリックで「差分・履歴」タブへ遷移。無ければ非クリック表示 */
  onJumpToHistory?: () => void;
}

type HoveredTraceabilityPreview =
  | { kind: "finding"; findingIndex: number; anchorRect: DOMRect }
  | {
      kind: "check";
      checkId: string;
      findingIndex: number;
      anchorRect: DOMRect;
    }
  | {
      kind: "evidence";
      checkId: string;
      evidenceIndex: number;
      findingIndex: number;
      anchorRect: DOMRect;
    };

const CRITICAL_COLLAPSE_THRESHOLD = 3;

// --- Helpers ---

interface TravelerInfo {
  name?: string;
  role?: string;
  age_group?: string;
  notes?: string;
  nationality?: string;
  nationality_region?: string;
  residence_country?: string;
  residence_region?: string;
  religion?: string;
  religious_and_cultural_note?: string;
  age?: number;
  birthday?: string;
  gender?: string;
  honorific?: string;
  job?: string;
  smoking_status?: string;
  japan_visit_times?: number;
  japan_visit_history?: string;
  is_primary?: boolean;
}

interface DataConflict {
  conflict_type?: string;
  severity?: string;
  description?: string;
  field_a?: string;
  field_b?: string;
  requires_confirmation?: boolean;
  note?: string;
}

interface FlightDetail {
  flight_code?: string;
  arrival_time?: string;
  departure_time?: string;
  airport?: string;
  source_field?: string;
  source_note?: string;
}

interface TravelerRelationship {
  person_a?: string;
  person_b?: string;
  relationship?: string;
  confidence?: string;
  evidence?: string;
}

function str(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

function categoryToTab(category?: CheckCategory): BasicRequestsTab {
  if (category === "need_review") return "review";
  if (category === "reminders") return "reminders";
  return "basic";
}

/**
 * `[check_id:xxx]` / `check_id:xxx` / 素の id を素の id に揃える。
 *
 * 引数を optional にしてあるのは、呼び出し側が配列の先頭を渡すため。
 * `source_check_ids` は**空配列が正常値**（ai-checks.yaml が「根拠が不明な
 * 場合は空配列とすること」と指示している）なので、`[0]` は undefined に
 * なりうる。tsconfig に noUncheckedIndexedAccess が無く型では防げない。
 */
function normalizeSourceCheckId(value: string | undefined): string {
  if (!value) return "";
  const trimmed = value.trim();
  const bracketMatch = trimmed.match(/^\[(?:check_id|rule_id):([^\]]+)\]$/);
  if (bracketMatch) {
    return bracketMatch[1].trim();
  }

  const plainMatch = trimmed.match(/^(?:check_id|rule_id):(.+)$/);
  if (plainMatch) {
    return plainMatch[1].trim();
  }

  return trimmed;
}

/**
 * finding の主根拠チェック ID を取り出す。無ければ undefined。
 *
 * **`Array.isArray` だけでは足りない。** `source_check_ids` は空配列が正常値で
 * （ai-checks.yaml が「根拠が不明な場合は空配列とすること」と指示している）、
 * `[0]` が undefined のまま normalizeSourceCheckId に渡ると `.trim()` で
 * 落ちる。findings の map の中なので、1 件でも該当すると**表示全体が
 * クラッシュする**（実際に dev で発生した）。
 */
export function primarySourceCheckIdOf(finding: {
  source_check_ids?: string[];
}): string | undefined {
  if (!Array.isArray(finding.source_check_ids)) return undefined;
  const normalized = normalizeSourceCheckId(finding.source_check_ids[0]);
  return normalized || undefined;
}

function ProfileCard({
  title,
  source,
  children,
}: {
  title: string;
  source?: CheckSource;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wider">
          {title}
        </h4>
        <SourceBadge source={source} />
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BadgeList({
  items,
  color,
}: {
  items: string[];
  color: "red" | "orange" | "green" | "yellow" | "blue" | "gray";
}) {
  const colorMap = {
    red: "bg-red-100 text-red-800",
    orange: "bg-orange-100 text-orange-800",
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
    gray: "bg-gray-100 text-gray-700",
  };
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorMap[color]}`}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function SourceEvidence({
  sourceField,
  sourceNote,
  contactTopics,
  labelResolver,
}: {
  sourceField?: string;
  sourceNote?: string;
  contactTopics?: ContactTopic[];
  labelResolver?: EvidenceLabelResolver;
}) {
  if (!sourceField && !sourceNote) return null;
  const formattedSource = sourceField
    ? (labelResolver?.formatSource(sourceField) ?? {
        display: sourceField,
        raw: null,
      })
    : null;
  return (
    <div className="mt-1 text-xs text-gray-400 space-y-0.5">
      {sourceField && (
        <div className="space-y-0.5">
          <div className="flex items-start gap-1">
            <span className="text-gray-300">Source:</span>
            <span className="text-gray-500 break-words">
              {formattedSource?.display ?? sourceField}
            </span>
          </div>
          {formattedSource?.raw && (
            <div className="pl-[42px] font-mono text-[11px] text-gray-300 break-all">
              {formattedSource.raw}
            </div>
          )}
        </div>
      )}
      {sourceNote && (
        <p className="break-words whitespace-pre-wrap italic text-gray-400">
          &ldquo;{sourceNote}&rdquo;
        </p>
      )}
      {sourceField && contactTopics && (
        <ContactTopicTrace
          sourceField={sourceField}
          contactTopics={contactTopics}
          labelResolver={labelResolver}
        />
      )}
    </div>
  );
}

function SourceFieldText({
  sourceField,
  labelResolver,
}: {
  sourceField: string;
  labelResolver?: EvidenceLabelResolver;
}) {
  const formatted = labelResolver?.formatSource(sourceField) ?? {
    display: sourceField,
    raw: null,
  };

  return (
    <div className="space-y-0.5">
      <div className="text-gray-500 break-words">{formatted.display}</div>
      {formatted.raw && (
        <div className="font-mono text-[11px] text-gray-300 break-all">
          {formatted.raw}
        </div>
      )}
    </div>
  );
}

function FlightEntry({
  direction,
  detail,
  contactTopics,
  labelResolver,
}: {
  direction: "arrival" | "departure";
  detail: FlightDetail;
  contactTopics?: ContactTopic[];
  labelResolver?: EvidenceLabelResolver;
}) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const Icon = direction === "arrival" ? PlaneLanding : PlaneTakeoff;
  const iconColor =
    direction === "arrival" ? "text-blue-500" : "text-orange-500";
  const label = direction === "arrival" ? "Arrival" : "Departure";
  const time =
    direction === "arrival" ? detail.arrival_time : detail.departure_time;
  const hasEvidence = !!(str(detail.source_field) || str(detail.source_note));

  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className={`${iconColor} mt-0.5 shrink-0`} />
      <div className="space-y-1 min-w-0">
        <div className="text-xs text-gray-500 font-medium">{label}</div>
        <div className="text-sm text-gray-900 font-medium">
          {str(detail.flight_code)}
        </div>
        {(str(time) || str(detail.airport)) && (
          <div className="text-xs text-gray-500">
            {[str(time), str(detail.airport)].filter(Boolean).join(" · ")}
          </div>
        )}
        {hasEvidence && (
          <div>
            <button
              type="button"
              className="text-[10px] text-gray-400 hover:text-gray-500 transition-colors flex items-center gap-0.5"
              onClick={() => setEvidenceOpen((v) => !v)}
            >
              Evidence
              {evidenceOpen ? (
                <ChevronUp size={10} />
              ) : (
                <ChevronDown size={10} />
              )}
            </button>
            {evidenceOpen && (
              <SourceEvidence
                sourceField={str(detail.source_field) || undefined}
                sourceNote={str(detail.source_note) || undefined}
                contactTopics={contactTopics}
                labelResolver={labelResolver}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Extracts a topic_id from a source_field like "contact_topics.dietary_restrictions" or "contact_topics[0]" */
function findMatchingTopics(
  sourceField: string,
  contactTopics: ContactTopic[],
): ContactTopic[] {
  if (!sourceField.includes("contact_topic")) return [];
  // Try to extract topic_id: "contact_topics.dietary_restrictions..." → "dietary_restrictions"
  const dotMatch = sourceField.match(/contact_topics?\.(\w+)/);
  if (dotMatch) {
    const topicId = dotMatch[1];
    const exact = contactTopics.filter((t) => t.topic_id === topicId);
    if (exact.length > 0) return exact;
    // Fuzzy: topic_id contains the extracted string
    const fuzzy = contactTopics.filter((t) => t.topic_id.includes(topicId));
    if (fuzzy.length > 0) return fuzzy;
  }
  // Try index: "contact_topics[0]" → index 0
  const idxMatch = sourceField.match(/contact_topics?\[(\d+)\]/);
  if (idxMatch) {
    const idx = Number.parseInt(idxMatch[1], 10);
    if (idx >= 0 && idx < contactTopics.length) return [contactTopics[idx]];
  }
  // Generic reference to contact_topics — no specific match
  return [];
}

function OriginalSourceRow({
  src,
  labelResolver,
}: {
  src: EvidenceSource;
  labelResolver?: EvidenceLabelResolver;
}) {
  return (
    <div className="text-xs text-gray-500 pl-2 border-l-2 border-indigo-200 space-y-0.5">
      <div className="text-gray-400">
        {labelResolver?.formatContactSourceSummary(src) ?? "Contact History"}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {src.source_type && (
          <span className="inline-flex items-center px-1 py-0.5 rounded bg-indigo-50 text-indigo-600">
            {labelResolver?.formatContactType(src.source_type) ??
              src.source_type}
          </span>
        )}
        {src.contacted_at && (
          <span className="text-gray-400">{src.contacted_at.slice(0, 10)}</span>
        )}
        {src.contact_purpose && (
          <span className="inline-flex items-center px-1 py-0.5 rounded bg-gray-100 text-gray-600">
            {labelResolver?.formatContactPurpose(src.contact_purpose) ??
              src.contact_purpose}
          </span>
        )}
        {src.source_id && (
          <span className="font-mono text-gray-300">{src.source_id}</span>
        )}
      </div>
      {src.quote && (
        <p className="italic text-gray-500 break-words whitespace-pre-wrap">
          &ldquo;{src.quote}&rdquo;
        </p>
      )}
    </div>
  );
}

function AllContactTopicsDialog({
  contactTopicsResult,
  onClose,
  labelResolver,
}: {
  contactTopicsResult: ContactTopicsResult;
  onClose: () => void;
  labelResolver?: EvidenceLabelResolver;
}) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  const { contactTopics } = contactTopicsResult;
  const totalSources = contactTopics.reduce(
    (sum, t) => sum + t.evidences.reduce((s, e) => s + e.sources.length, 0),
    0,
  );

  const selectedTopic = contactTopics.find(
    (t) => t.topic_id === selectedTopicId,
  );

  return (
    <DialogShell
      onClose={onClose}
      scrollLock="body"
      closeOnBackdrop={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      panelClassName="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col mx-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-bold text-gray-800">
            All Contact Topics
          </h2>
          <span className="text-xs text-gray-400">
            {contactTopics.length} topics · {totalSources} sources
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* (i) metadata popover */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMetadataOpen((v) => !v)}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-300 hover:text-gray-500"
              title="Metadata"
            >
              <AlertCircle size={14} />
            </button>
            {metadataOpen && (
              <div className="absolute right-0 top-full mt-1 z-10 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs space-y-1">
                <div>
                  <span className="text-gray-400">Model:</span>{" "}
                  <span className="text-gray-700 font-mono">
                    {contactTopicsResult.model ?? "—"}
                  </span>
                  {contactTopicsResult.fallbackFrom && (
                    <span className="text-orange-500 ml-1">
                      (from {contactTopicsResult.fallbackFrom})
                    </span>
                  )}
                </div>
                <div>
                  <span className="text-gray-400">Tokens:</span>{" "}
                  <span className="text-gray-700 font-mono">
                    {contactTopicsResult.tokensUsed.prompt} /{" "}
                    {contactTopicsResult.tokensUsed.completion} /{" "}
                    {contactTopicsResult.tokensUsed.total}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Duration:</span>{" "}
                  <span className="text-gray-700 font-mono">
                    {contactTopicsResult.durationMs != null
                      ? `${(contactTopicsResult.durationMs / 1000).toFixed(1)}s`
                      : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Last Checked:</span>{" "}
                  <span className="text-gray-700">
                    {contactTopicsResult.lastCheckedAt}
                  </span>
                </div>
                {contactTopicsResult.compactionDegraded && (
                  <div className="text-orange-600 font-medium">
                    ⚠ Compaction degraded
                  </div>
                )}
                {contactTopicsResult.error && (
                  <div className="text-red-600">
                    Error: {contactTopicsResult.error}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Master-detail body */}
      <div className="flex min-h-0 flex-1 select-text">
        {/* Master: topic list */}
        <div className="w-[360px] shrink-0 border-r border-gray-200 overflow-y-auto">
          {contactTopics.map((topic) => {
            const isSelected = topic.topic_id === selectedTopicId;
            return (
              <button
                key={topic.topic_id}
                type="button"
                onClick={() =>
                  setSelectedTopicId(isSelected ? null : topic.topic_id)
                }
                className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                  isSelected
                    ? "bg-indigo-50 border-l-2 border-l-indigo-500"
                    : "hover:bg-gray-50 border-l-2 border-l-transparent"
                }`}
              >
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600 text-[10px] font-mono">
                  {topic.topic_id}
                </span>
                <div className="text-xs text-gray-800 mt-1">
                  {topic.topic_summary || topic.topic_id}
                </div>
              </button>
            );
          })}
          {contactTopics.length === 0 && (
            <div className="text-sm text-gray-400 text-center py-8">
              No topics
            </div>
          )}
        </div>

        {/* Detail: evidences of selected topic */}
        <div className="flex-1 overflow-y-auto">
          {selectedTopic ? (
            <div className="p-5 space-y-4">
              {selectedTopic.evidences.map((ev) => (
                <div key={ev.evidence_id} className="space-y-2">
                  {ev.evidence_summary && (
                    <div className="text-xs text-gray-700 font-medium">
                      {ev.evidence_summary}
                    </div>
                  )}
                  <div className="space-y-1.5 ml-1">
                    {ev.sources.map((src, si) => (
                      <OriginalSourceRow
                        key={`${ev.evidence_id}-${si}`}
                        src={src}
                        labelResolver={labelResolver}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-400">
              Select a topic to view evidences
            </div>
          )}
        </div>
      </div>
    </DialogShell>
  );
}

function ContactTopicTrace({
  sourceField,
  contactTopics,
  labelResolver,
}: {
  sourceField: string;
  contactTopics: ContactTopic[];
  labelResolver?: EvidenceLabelResolver;
}) {
  const [open, setOpen] = useState(false);
  const matched = useMemo(
    () => findMatchingTopics(sourceField, contactTopics),
    [sourceField, contactTopics],
  );
  const allSources = matched.flatMap((t) =>
    t.evidences.flatMap((e) => e.sources),
  );

  if (allSources.length === 0) return null;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 transition-colors font-medium"
      >
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        Original contacts ({allSources.length})
      </button>
      {open && (
        <div className="mt-1.5 space-y-2 ml-1">
          {matched.map((topic) => (
            <div key={topic.topic_id}>
              {matched.length > 1 && (
                <div className="text-xs font-medium text-indigo-600 mb-1">
                  {topic.topic_id}
                  {topic.topic_summary && (
                    <span className="font-normal text-gray-400 ml-1">
                      — {topic.topic_summary}
                    </span>
                  )}
                </div>
              )}
              {topic.evidences.map((ev) => (
                <div key={ev.evidence_id} className="space-y-1.5">
                  {ev.evidence_summary && (
                    <div className="text-xs text-gray-500">
                      {ev.evidence_summary}
                    </div>
                  )}
                  {ev.sources.map((src, si) => (
                    <OriginalSourceRow
                      key={`${ev.evidence_id}-${si}`}
                      src={src}
                      labelResolver={labelResolver}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailedItemList({
  items,
  color,
  labelKey,
  personKey = "person",
  sourceFieldKey = "source_field",
  sourceNoteKey = "source_note",
  renderExtra,
  contactTopics,
  labelResolver,
}: {
  items: Array<Record<string, unknown>>;
  color: "red" | "orange" | "green" | "yellow" | "blue";
  labelKey: string;
  personKey?: string;
  sourceFieldKey?: string;
  sourceNoteKey?: string;
  renderExtra?: (item: Record<string, unknown>) => React.ReactNode;
  contactTopics?: ContactTopic[];
  labelResolver?: EvidenceLabelResolver;
}) {
  const colorMap = {
    red: "border-red-200 bg-red-50/50",
    orange: "border-orange-200 bg-orange-50/50",
    green: "border-green-200 bg-green-50/50",
    yellow: "border-yellow-200 bg-yellow-50/50",
    blue: "border-blue-200 bg-blue-50/50",
  };
  const badgeColorMap = {
    red: "bg-red-100 text-red-800",
    orange: "bg-orange-100 text-orange-800",
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
  };
  if (items.length === 0) {
    return <span className="text-sm text-gray-300">&mdash;</span>;
  }
  const hasAnyEvidence = items.some(
    (item) => str(item[sourceFieldKey]) || str(item[sourceNoteKey]),
  );
  if (!hasAnyEvidence) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span
            key={i}
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColorMap[color]}`}
          >
            {str(item[labelKey])}
            {str(item[personKey]) ? ` (${str(item[personKey])})` : ""}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div
          key={i}
          className={`rounded-md border px-3 py-2 ${colorMap[color]}`}
        >
          <div className="flex items-center gap-2 text-sm">
            <span
              className={`font-medium ${badgeColorMap[color].split(" ")[1]}`}
            >
              {str(item[labelKey])}
            </span>
            {str(item[personKey]) && (
              <span className="text-gray-500 text-xs">
                ({str(item[personKey])})
              </span>
            )}
          </div>
          {renderExtra?.(item)}
          <SourceEvidence
            sourceField={str(item[sourceFieldKey]) || undefined}
            sourceNote={str(item[sourceNoteKey]) || undefined}
            contactTopics={contactTopics}
            labelResolver={labelResolver}
          />
        </div>
      ))}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 shrink-0">{label}:</span>
      <span className={value ? "text-gray-900" : "text-gray-300"}>
        {value || "—"}
      </span>
    </div>
  );
}

function ContactCompactionSummaryRow({
  contactTopicsResult,
  onOpenAll,
}: {
  contactTopicsResult: ContactTopicsResult;
  onOpenAll: () => void;
}) {
  const topicCount = contactTopicsResult.contactTopics.length;
  return (
    <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-indigo-800">
            Contact Compaction (All Topics)
          </div>
          <div className="text-xs text-indigo-600">
            {topicCount} topics · {contactTopicsResult.tokensUsed.total} tokens
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenAll}
          className="shrink-0 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors font-medium"
        >
          Open
        </button>
      </div>
    </div>
  );
}

function ExtractedNotesList({
  notes,
  contactTopics,
  labelResolver,
}: {
  notes: ExtractedNote[];
  contactTopics?: ContactTopic[];
  labelResolver?: EvidenceLabelResolver;
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  if (notes.length === 0) {
    return <span className="text-sm text-gray-300">&mdash;</span>;
  }

  const importanceColor = (imp: string) => {
    if (imp === "high") return "bg-red-100 text-red-700";
    if (imp === "low") return "bg-gray-100 text-gray-500";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="space-y-1.5">
      {notes.map((note, i) => {
        const open = expandedIdx === i;
        return (
          <div key={i} className="border border-gray-100 rounded-md">
            <button
              type="button"
              onClick={() => setExpandedIdx(open ? null : i)}
              className="w-full flex items-start gap-2 p-2.5 pl-[16px] text-left text-sm hover:bg-gray-50 transition-colors"
            >
              <span className="flex-1 min-w-0 pl-[6px]">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium -ml-[6px] mr-1 align-middle ${importanceColor(note.importance)}`}
                >
                  {note.importance}
                </span>
                <span className="font-semibold text-gray-800">
                  {note.topic}
                </span>
                {note.summary && (
                  <span className="text-gray-600"> — {note.summary}</span>
                )}
              </span>
              <span className="shrink-0 mt-0.5">
                {open ? (
                  <ChevronUp size={14} className="text-gray-400" />
                ) : (
                  <ChevronDown size={14} className="text-gray-400" />
                )}
              </span>
            </button>
            {open && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                <p className="text-sm text-gray-700 mt-2">{note.detail}</p>
                {Array.isArray(note.sources) && note.sources.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Sources ({note.sources.length})
                    </div>
                    {note.sources.map((src, j) => (
                      <div
                        key={j}
                        className="text-xs text-gray-500 pl-2 border-l-2 border-gray-200 space-y-0.5"
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          {src.type && (
                            <span className="inline-flex items-center px-1 py-0.5 rounded bg-gray-100 text-gray-600">
                              {src.type}
                            </span>
                          )}
                          {src.date && (
                            <span className="text-gray-400">{src.date}</span>
                          )}
                          {src.source_field && (
                            <SourceFieldText
                              sourceField={src.source_field}
                              labelResolver={labelResolver}
                            />
                          )}
                        </div>
                        {src.excerpt && (
                          <p className="italic text-gray-500 break-words whitespace-pre-wrap">
                            &ldquo;{src.excerpt}&rdquo;
                          </p>
                        )}
                        {contactTopics && contactTopics.length > 0 && (
                          <ContactTopicTrace
                            sourceField={src.source_field || ""}
                            contactTopics={contactTopics}
                            labelResolver={labelResolver}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SpecialRequestsList({
  requests,
  contactTopics,
  labelResolver,
}: {
  requests: Array<Record<string, string>>;
  contactTopics?: ContactTopic[];
  labelResolver?: EvidenceLabelResolver;
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  return (
    <div className="space-y-1.5">
      {requests.map((req, i) => {
        const open = expandedIdx === i;
        const hasEvidence = req.source_field || req.source_note;
        const hasTopics = contactTopics && contactTopics.length > 0;
        const canExpand = hasEvidence || hasTopics;
        return (
          <div key={i} className="border border-gray-100 rounded-md">
            <button
              type="button"
              onClick={() => canExpand && setExpandedIdx(open ? null : i)}
              className={`w-full flex items-start gap-2 p-2.5 text-left text-sm ${
                canExpand ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
              } transition-colors`}
            >
              <span className="flex-1 min-w-0">
                <span className="text-gray-800">{req.request}</span>
                {req.person && (
                  <span className="text-gray-400 ml-1.5 text-xs">
                    ({req.person})
                  </span>
                )}
              </span>
              {canExpand && (
                <span className="shrink-0 mt-0.5">
                  {open ? (
                    <ChevronUp size={14} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={14} className="text-gray-400" />
                  )}
                </span>
              )}
            </button>
            {open && canExpand && (
              <div className="px-3 pb-3 border-t border-gray-100 space-y-2">
                {req.source_field && (
                  <div className="mt-2 text-xs space-y-0.5">
                    <span className="text-gray-300 font-medium">Source:</span>
                    <SourceFieldText
                      sourceField={req.source_field}
                      labelResolver={labelResolver}
                    />
                  </div>
                )}
                {req.source_note && (
                  <div className="mt-1">
                    <div className="text-xs text-gray-300 font-medium mb-1">
                      Original Text
                    </div>
                    <p className="text-xs text-gray-500 break-words whitespace-pre-wrap italic bg-gray-50 rounded p-2 border border-gray-100">
                      {req.source_note}
                    </p>
                  </div>
                )}
                {hasTopics && (
                  <ContactTopicTrace
                    sourceField={req.source_field || ""}
                    contactTopics={contactTopics}
                    labelResolver={labelResolver}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ia-ui 第2波: 縦長対策。Executive Summary 以外の大きなセクションを既定で折りたたむ。
// 折りたたみヘッダに重症度アイコン・件数バッジなどのサマリ情報を残す。
function CollapsibleBlock({
  title,
  badge,
  open,
  onToggle,
  children,
}: {
  title: React.ReactNode;
  badge?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-gray-700">
          {open ? (
            <ChevronDown size={15} className="text-gray-400 shrink-0" />
          ) : (
            <ChevronRight size={15} className="text-gray-400 shrink-0" />
          )}
          {title}
        </span>
        {badge}
      </button>
      {open && <div className="border-t border-gray-100 p-4">{children}</div>}
    </div>
  );
}

// --- Main Component ---

export function BasicRequestsView({
  result,
  showExecutiveSummary = true,
  onJumpToTab,
  onTraceabilityHistoryPush,
  onReturnToSummary,
  onProjectDataReferenceClick,
  onJumpToHistory,
}: BasicRequestsViewProps) {
  const [showAllCritical, setShowAllCritical] = useState(false);
  // ia-ui 第2波: セクション折りたたみ（Executive Summary 以外は既定で閉）
  const [dataConflictsOpen, setDataConflictsOpen] = useState(false);
  const [criticalOpen, setCriticalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [checkResultsOpen, setCheckResultsOpen] = useState(false);
  const { dismiss, restore, isDismissed, dismissedCount } = useDismissedChecks(
    result.projectId,
  );
  const [checkStatusFilter, setCheckStatusFilter] = useState<string[]>([]);
  const [hideCheckDismissed, setHideCheckDismissed] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [pendingScrollCheckId, setPendingScrollCheckId] = useState<
    string | null
  >(null);
  const [pendingEvidenceTarget, setPendingEvidenceTarget] =
    useState<FocusedEvidenceTarget | null>(null);
  const [hoveredPreview, setHoveredPreview] =
    useState<HoveredTraceabilityPreview | null>(null);
  const [highlightedSummaryFindingIndex, setHighlightedSummaryFindingIndex] =
    useState<number | null>(null);
  const focusedSummaryFindingIndex = useAICheckStore(
    (state) => state.focusedSummaryFindingIndex,
  );
  const focusedCheckId = useAICheckStore((state) => state.focusedCheckId);
  const focusedEvidenceTarget = useAICheckStore(
    (state) => state.focusedEvidenceTarget,
  );
  const highlightCheckId = useAICheckStore((state) => state.highlightCheckId);
  const highlightEvidenceTarget = useAICheckStore(
    (state) => state.highlightEvidenceTarget,
  );
  const contactTopics: ContactTopic[] =
    result.contactTopicsResult?.contactTopics ?? [];
  const labelResolver = useMemo(
    () =>
      createEvidenceLabelResolver({
        projectData: result.projectData,
        contactTopics,
      }),
    [contactTopics, result.projectData],
  );
  const summaryReturnTarget = useAICheckStore(
    (state) => state.summaryReturnTarget,
  );
  const setFocusedSummaryFindingIndex = useAICheckStore(
    (state) => state.setFocusedSummaryFindingIndex,
  );
  const setFocusedCheckId = useAICheckStore((state) => state.setFocusedCheckId);
  const setFocusedEvidenceTarget = useAICheckStore(
    (state) => state.setFocusedEvidenceTarget,
  );
  const setHighlightCheckId = useAICheckStore(
    (state) => state.setHighlightCheckId,
  );
  const setHighlightEvidenceTarget = useAICheckStore(
    (state) => state.setHighlightEvidenceTarget,
  );
  const setSummaryReturnTarget = useAICheckStore(
    (state) => state.setSummaryReturnTarget,
  );
  const summaryFindingsDetailsRef = useRef<HTMLDetailsElement>(null);
  const checkResultsSectionRef = useRef<HTMLDivElement>(null);
  const checkResultsListRef = useRef<HTMLDivElement>(null);
  const citationCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Extract executive summary separately from other general checks
  const basicRequestsSummary = result.aiResults.find(
    (r) => r.checkId === "basic-requests-summary",
  );
  const basicRequestsSummaryData = basicRequestsSummary?.result as
    | BasicRequestsSummaryResult
    | undefined;
  const basicRequestsFindings = Array.isArray(
    basicRequestsSummaryData?.findings,
  )
    ? basicRequestsSummaryData.findings
    : [];
  const basicRequestsSummarySections = Array.isArray(
    basicRequestsSummaryData?.summary_sections,
  )
    ? basicRequestsSummaryData.summary_sections
    : undefined;
  const generalAI = result.aiResults.filter(
    (r) => r.category === "general" && r.checkId !== "basic-requests-summary",
  );
  const generalConsistency = result.consistencyResults.filter(
    (r) => r.category === "general",
  );
  const checkReferenceMap = useMemo(() => {
    const map = new Map<string, { label: string; category: CheckCategory }>();

    for (const aiResult of result.aiResults) {
      map.set(aiResult.checkId, {
        label: aiResult.checkName,
        category: aiResult.category,
      });
    }

    for (const consistencyResult of result.consistencyResults) {
      map.set(consistencyResult.ruleId, {
        label: consistencyResult.ruleName,
        category: consistencyResult.category,
      });
    }

    return map;
  }, [result.aiResults, result.consistencyResults]);
  const evidenceEntriesByCheckId = useMemo(() => {
    const map = new Map<string, EvidenceTraceEntry[]>();
    for (const aiResult of result.aiResults) {
      map.set(aiResult.checkId, collectEvidenceEntries(aiResult.result));
    }
    return map;
  }, [result.aiResults]);
  const previewReferenceMap = useMemo(() => {
    const map = new Map<
      string,
      {
        title: string;
        detail: string;
        source: CheckSource;
        category: CheckCategory;
      }
    >();

    for (const aiResult of result.aiResults) {
      map.set(aiResult.checkId, {
        title: aiResult.checkName,
        detail: aiResult.summary ?? aiResult.error ?? "No details available",
        source: "ai",
        category: aiResult.category,
      });
    }

    for (const consistencyResult of result.consistencyResults) {
      map.set(consistencyResult.ruleId, {
        title: consistencyResult.ruleName,
        detail: consistencyResult.message,
        source: "rule",
        category: consistencyResult.category,
      });
    }

    return map;
  }, [result.aiResults, result.consistencyResults]);

  const criticalChecks = useMemo(() => {
    const checks: Array<{
      title: string;
      description: string;
      severity: "high" | "medium";
      status: string;
      importance: string;
      source: CheckSource;
    }> = [];

    for (const r of generalAI) {
      if (r.status === "fail" || r.status === "error") {
        checks.push({
          title: r.checkName,
          description: r.summary ?? r.error ?? "",
          severity: "high",
          status: r.status,
          importance: r.importance ?? "medium",
          source: "ai",
        });
      } else if (r.status === "warning") {
        checks.push({
          title: r.checkName,
          description: r.summary ?? "",
          severity: "medium",
          status: r.status,
          importance: r.importance ?? "medium",
          source: "ai",
        });
      }
    }

    for (const r of generalConsistency) {
      if (!r.passed) {
        checks.push({
          title: r.ruleName,
          description: r.evaluationError
            ? `Evaluation error: ${r.message}`
            : r.message,
          severity: r.severity === "error" ? "high" : "medium",
          status: r.evaluationError ? "error" : r.severity,
          importance: r.importance ?? "medium",
          source: "rule",
        });
      }
    }

    checks.sort(compareByStatusAndImportance);
    return checks;
  }, [generalAI, generalConsistency]);

  // --- Check Results with filter/dismiss ---

  interface CheckItem {
    id: string;
    title: string;
    detail: string;
    severity: "high" | "medium" | "low";
    status: string;
    importance: string;
    score?: number;
    rawAIScore?: number;
    scoreAdjusted?: boolean;
    rawResult?: Record<string, unknown>;
    evaluationError?: boolean;
    source: CheckSource;
  }

  const checkItems = useMemo(() => {
    const items: CheckItem[] = [];
    for (const r of generalAI) {
      items.push({
        id: r.checkId,
        title: r.checkName,
        detail: r.summary ?? r.error ?? "No details available",
        severity:
          r.status === "fail" || r.status === "error"
            ? "high"
            : r.status === "warning"
              ? "medium"
              : "low",
        status: r.status,
        importance: r.importance ?? "medium",
        score: r.overallScore,
        rawAIScore: r.rawAIScore,
        scoreAdjusted: r.scoreAdjusted,
        rawResult: r.result,
        source: "ai",
      });
    }
    for (const r of generalConsistency) {
      items.push({
        id: r.ruleId,
        title: r.ruleName,
        detail: r.message,
        severity: !r.passed
          ? r.severity === "error"
            ? "high"
            : "medium"
          : "low",
        status: r.evaluationError ? "error" : r.passed ? "pass" : r.severity,
        importance: r.importance ?? "medium",
        evaluationError: r.evaluationError,
        source: "rule",
      });
    }
    items.sort(compareByStatusAndImportance);
    return items;
  }, [generalAI, generalConsistency]);

  const checkErrorItems = useMemo(
    () => checkItems.filter((item) => item.status === "error"),
    [checkItems],
  );
  const checkNormalItems = useMemo(
    () => checkItems.filter((item) => item.status !== "error"),
    [checkItems],
  );

  const checkStatusOptions = useMemo(() => {
    const order = ["fail", "warning", "pass"];
    const counts = new Map<string, number>();
    for (const item of checkNormalItems) {
      if (!isDismissed(item.id)) {
        counts.set(item.status, (counts.get(item.status) ?? 0) + 1);
      }
    }
    return order
      .filter((status) => counts.has(status))
      .map((status) => ({ status, count: counts.get(status) ?? 0 }));
  }, [checkNormalItems, isDismissed]);

  const checkFilteredItems = useMemo(() => {
    if (checkStatusFilter.length === 0) return checkNormalItems;
    return checkNormalItems.filter((item) =>
      checkStatusFilter.includes(item.status),
    );
  }, [checkNormalItems, checkStatusFilter]);

  const { checkActiveItems, checkDismissedItems } = useMemo(() => {
    const active: CheckItem[] = [];
    const dismissed: CheckItem[] = [];
    for (const item of checkFilteredItems) {
      if (isDismissed(item.id)) dismissed.push(item);
      else active.push(item);
    }
    return { checkActiveItems: active, checkDismissedItems: dismissed };
  }, [checkFilteredItems, isDismissed]);

  const checkActiveCount = checkNormalItems.filter(
    (i) => !isDismissed(i.id),
  ).length;

  const selectedCheckItem =
    checkItems.find((i) => i.id === selectedCheckId) ?? null;
  const isPendingScrollVisible = useMemo(() => {
    if (!pendingScrollCheckId) return false;
    if (checkErrorItems.some((item) => item.id === pendingScrollCheckId)) {
      return true;
    }
    if (checkActiveItems.some((item) => item.id === pendingScrollCheckId)) {
      return true;
    }
    return (
      !hideCheckDismissed &&
      checkDismissedItems.some((item) => item.id === pendingScrollCheckId)
    );
  }, [
    checkActiveItems,
    checkDismissedItems,
    checkErrorItems,
    hideCheckDismissed,
    pendingScrollCheckId,
  ]);

  const resolveCheckReference = useCallback(
    (checkId: string) => {
      const normalizedCheckId = normalizeSourceCheckId(checkId);
      const match = checkReferenceMap.get(normalizedCheckId);
      return match
        ? {
            id: normalizedCheckId,
            label: match.label,
            category: match.category,
            available: true,
          }
        : {
            id: normalizedCheckId,
            label: normalizedCheckId,
            category: undefined,
            available: false,
          };
    },
    [checkReferenceMap],
  );

  const ensureSummaryFindingHistory = useCallback(
    (findingIndex: number) => {
      if (!onTraceabilityHistoryPush) return;

      const currentTraceabilityState =
        typeof window !== "undefined" &&
        typeof window.history.state === "object" &&
        window.history.state !== null
          ? (window.history.state as Record<string, unknown>)
              .__aicheckTraceability
          : null;
      const currentState =
        typeof currentTraceabilityState === "object" &&
        currentTraceabilityState !== null
          ? (currentTraceabilityState as Record<string, unknown>)
          : null;

      if (
        currentState?.kind === "summary-finding" &&
        currentState.tab === "basic" &&
        currentState.summaryFindingIndex === findingIndex
      ) {
        return;
      }

      onTraceabilityHistoryPush({
        kind: "summary-finding",
        tab: "basic",
        summaryFindingIndex: findingIndex,
      });
    },
    [onTraceabilityHistoryPush],
  );

  const handleJumpToCheck = useCallback(
    (
      checkId: string,
      options?: { findingIndex?: number; pushHistory?: boolean },
    ) => {
      const target = resolveCheckReference(checkId);
      if (!target.available) return;
      const tab = categoryToTab(target.category);
      if (options?.findingIndex != null) {
        setSummaryReturnTarget({
          tab,
          checkId: target.id,
          findingIndex: options.findingIndex,
        });
        if (options.pushHistory !== false) {
          ensureSummaryFindingHistory(options.findingIndex);
          onTraceabilityHistoryPush?.({
            kind: "check",
            tab,
            checkId: target.id,
            returnFindingIndex: options.findingIndex,
          });
        }
      }
      onJumpToTab?.(tab);
      setFocusedCheckId(target.id);
    },
    [
      onJumpToTab,
      onTraceabilityHistoryPush,
      ensureSummaryFindingHistory,
      resolveCheckReference,
      setFocusedCheckId,
      setSummaryReturnTarget,
    ],
  );

  const handleJumpToEvidence = useCallback(
    (checkId: string, evidenceIndex: number, findingIndex: number) => {
      const target = resolveCheckReference(checkId);
      if (!target.available) return;

      const tab = categoryToTab(target.category);
      const evidenceTarget = { checkId: target.id, evidenceIndex };
      setSummaryReturnTarget({
        tab,
        checkId: target.id,
        findingIndex,
      });
      setFocusedEvidenceTarget(evidenceTarget);
      ensureSummaryFindingHistory(findingIndex);
      onTraceabilityHistoryPush?.({
        kind: "evidence",
        tab,
        checkId: target.id,
        evidenceIndex,
        returnFindingIndex: findingIndex,
      });
      onJumpToTab?.(tab);
    },
    [
      onJumpToTab,
      onTraceabilityHistoryPush,
      ensureSummaryFindingHistory,
      resolveCheckReference,
      setFocusedEvidenceTarget,
      setSummaryReturnTarget,
    ],
  );

  function handleManualCheckSelect(checkId: string) {
    setSummaryReturnTarget(null);
    setPendingEvidenceTarget(null);
    setHighlightEvidenceTarget(null);
    setSelectedCheckId((current) => (current === checkId ? null : checkId));
  }

  const clearCitationCloseTimer = useCallback(() => {
    if (citationCloseTimerRef.current) {
      clearTimeout(citationCloseTimerRef.current);
      citationCloseTimerRef.current = null;
    }
  }, []);

  const openPreview = useCallback(
    (preview: HoveredTraceabilityPreview) => {
      clearCitationCloseTimer();
      setHoveredPreview(preview);
    },
    [clearCitationCloseTimer],
  );

  const openCitationPreview = useCallback(
    (findingIndex: number, anchorRect: DOMRect) => {
      openPreview({ kind: "finding", findingIndex, anchorRect });
    },
    [openPreview],
  );

  const openCheckPreview = useCallback(
    (checkId: string, findingIndex: number, anchorRect: DOMRect) => {
      openPreview({ kind: "check", checkId, findingIndex, anchorRect });
    },
    [openPreview],
  );

  const openEvidencePreview = useCallback(
    (
      checkId: string,
      evidenceIndex: number,
      findingIndex: number,
      anchorRect: DOMRect,
    ) => {
      openPreview({
        kind: "evidence",
        checkId,
        evidenceIndex,
        findingIndex,
        anchorRect,
      });
    },
    [openPreview],
  );

  const scheduleCitationPreviewClose = useCallback(() => {
    clearCitationCloseTimer();
    citationCloseTimerRef.current = setTimeout(() => {
      setHoveredPreview(null);
      citationCloseTimerRef.current = null;
    }, 120);
  }, [clearCitationCloseTimer]);

  const handleScrollToSummaryFinding = useCallback(
    (findingIndex: number, pushHistory = true) => {
      setHoveredPreview(null);

      if (pushHistory) {
        onTraceabilityHistoryPush?.({
          kind: "summary-finding",
          tab: "basic",
          summaryFindingIndex: findingIndex,
        });
      }

      const detailsElement = summaryFindingsDetailsRef.current;
      if (detailsElement && !detailsElement.open) {
        detailsElement.open = true;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const target = document.getElementById(
            `executive-summary-finding-${findingIndex}`,
          );
          if (!target) return;
          setHighlightedSummaryFindingIndex(findingIndex);
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      });
    },
    [onTraceabilityHistoryPush],
  );

  const handlePreviewClick = useCallback(() => {
    if (!hoveredPreview) return;

    if (hoveredPreview.kind === "finding") {
      handleScrollToSummaryFinding(hoveredPreview.findingIndex);
      return;
    }

    if (hoveredPreview.kind === "check") {
      handleJumpToCheck(hoveredPreview.checkId, {
        findingIndex: hoveredPreview.findingIndex,
      });
      return;
    }

    handleJumpToEvidence(
      hoveredPreview.checkId,
      hoveredPreview.evidenceIndex,
      hoveredPreview.findingIndex,
    );
  }, [
    handleJumpToCheck,
    handleJumpToEvidence,
    handleScrollToSummaryFinding,
    hoveredPreview,
  ]);

  const handleCitationHover = useCallback(
    (findingIndex: number, anchorRect: DOMRect) => {
      openCitationPreview(findingIndex, anchorRect);
    },
    [openCitationPreview],
  );

  const handleCitationLeave = useCallback(() => {
    scheduleCitationPreviewClose();
  }, [scheduleCitationPreviewClose]);

  const handleCitationClick = useCallback(
    (findingIndex: number) => {
      handleScrollToSummaryFinding(findingIndex);
    },
    [handleScrollToSummaryFinding],
  );

  const hoveredPreviewContent = useMemo(() => {
    if (!hoveredPreview) return null;

    if (hoveredPreview.kind === "finding") {
      const finding = basicRequestsFindings[hoveredPreview.findingIndex];
      if (!finding) return null;

      return {
        anchorRect: hoveredPreview.anchorRect,
        badgeLabel: finding.severity,
        badgeTone:
          finding.severity === "critical"
            ? "critical"
            : finding.severity === "warning"
              ? "warning"
              : "neutral",
        meta: finding.check_point,
        body: finding.finding,
      } as const;
    }

    if (hoveredPreview.kind === "check") {
      const preview = previewReferenceMap.get(hoveredPreview.checkId);
      if (!preview) return null;

      return {
        anchorRect: hoveredPreview.anchorRect,
        badgeLabel: preview.source === "rule" ? "Rule" : "Check",
        badgeTone: "info",
        title: preview.title,
        meta:
          preview.category === "general"
            ? "Basic Requests"
            : preview.category === "need_review"
              ? "Need Review"
              : "Reminders",
        body: preview.detail,
      } as const;
    }

    const evidenceEntry = (evidenceEntriesByCheckId.get(
      hoveredPreview.checkId,
    ) ?? [])[hoveredPreview.evidenceIndex];
    if (!evidenceEntry) return null;

    return {
      anchorRect: hoveredPreview.anchorRect,
      badgeLabel: "Evidence",
      badgeTone: "info",
      title: labelResolver.formatSource(evidenceEntry.evidence.source).display,
      body: labelResolver.formatQuote(
        evidenceEntry.evidence.quote,
        evidenceEntry.evidence.source,
      ),
    } as const;
  }, [
    basicRequestsFindings,
    evidenceEntriesByCheckId,
    hoveredPreview,
    labelResolver,
    previewReferenceMap,
  ]);

  const scrollToCheckResultsSection = useCallback(() => {
    const section = checkResultsSectionRef.current;
    if (!section) return;

    requestAnimationFrame(() => {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  useEffect(() => {
    if (!selectedCheckId) return;
    const stillVisible =
      checkActiveItems.some((item) => item.id === selectedCheckId) ||
      checkDismissedItems.some((item) => item.id === selectedCheckId) ||
      checkErrorItems.some((item) => item.id === selectedCheckId);
    if (!stillVisible) setSelectedCheckId(null);
  }, [checkActiveItems, checkDismissedItems, selectedCheckId, checkErrorItems]);

  useEffect(() => {
    if (
      !focusedCheckId ||
      !checkItems.some((item) => item.id === focusedCheckId)
    ) {
      return;
    }

    if (checkStatusFilter.length > 0) {
      setCheckStatusFilter([]);
    }
    if (hideCheckDismissed && isDismissed(focusedCheckId)) {
      setHideCheckDismissed(false);
    }

    setCheckResultsOpen(true);
    scrollToCheckResultsSection();
    setSelectedCheckId(focusedCheckId);
    setHighlightCheckId(focusedCheckId);
    setPendingScrollCheckId(focusedCheckId);
    setFocusedCheckId(null);
  }, [
    checkItems,
    checkStatusFilter,
    focusedCheckId,
    hideCheckDismissed,
    isDismissed,
    scrollToCheckResultsSection,
    setFocusedCheckId,
    setHighlightCheckId,
  ]);

  useEffect(() => {
    if (!pendingScrollCheckId || !isPendingScrollVisible) return;

    const element = document.getElementById(
      `check-card-${pendingScrollCheckId}`,
    );
    if (!element || !checkResultsListRef.current) {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      scrollElementIntoContainer(checkResultsListRef.current, element);
      setPendingScrollCheckId((current) =>
        current === pendingScrollCheckId ? null : current,
      );
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isPendingScrollVisible, pendingScrollCheckId]);

  useEffect(() => {
    if (focusedSummaryFindingIndex == null) return;
    handleScrollToSummaryFinding(focusedSummaryFindingIndex, false);
    setSummaryReturnTarget(null);
    setFocusedSummaryFindingIndex(null);
  }, [
    focusedSummaryFindingIndex,
    handleScrollToSummaryFinding,
    setFocusedSummaryFindingIndex,
    setSummaryReturnTarget,
  ]);

  useEffect(() => {
    if (
      !focusedEvidenceTarget ||
      !checkItems.some((item) => item.id === focusedEvidenceTarget.checkId)
    ) {
      return;
    }

    if (checkStatusFilter.length > 0) {
      setCheckStatusFilter([]);
    }
    if (hideCheckDismissed && isDismissed(focusedEvidenceTarget.checkId)) {
      setHideCheckDismissed(false);
    }

    setCheckResultsOpen(true);
    scrollToCheckResultsSection();
    setSelectedCheckId(focusedEvidenceTarget.checkId);
    setHighlightCheckId(focusedEvidenceTarget.checkId);
    setHighlightEvidenceTarget(focusedEvidenceTarget);
    setPendingScrollCheckId(focusedEvidenceTarget.checkId);
    setPendingEvidenceTarget(focusedEvidenceTarget);
    setFocusedEvidenceTarget(null);
  }, [
    checkItems,
    checkStatusFilter,
    focusedEvidenceTarget,
    hideCheckDismissed,
    isDismissed,
    scrollToCheckResultsSection,
    setFocusedEvidenceTarget,
    setHighlightCheckId,
    setHighlightEvidenceTarget,
  ]);

  useEffect(() => {
    if (
      !highlightCheckId ||
      !checkItems.some((item) => item.id === highlightCheckId)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightCheckId(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [checkItems, highlightCheckId, setHighlightCheckId]);

  useEffect(() => {
    if (
      !highlightEvidenceTarget ||
      !checkItems.some((item) => item.id === highlightEvidenceTarget.checkId)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightEvidenceTarget(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [checkItems, highlightEvidenceTarget, setHighlightEvidenceTarget]);

  useEffect(() => {
    return () => {
      if (citationCloseTimerRef.current) {
        clearTimeout(citationCloseTimerRef.current);
        citationCloseTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (highlightedSummaryFindingIndex == null) return;

    const timer = window.setTimeout(() => {
      setHighlightedSummaryFindingIndex(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [highlightedSummaryFindingIndex]);

  const profile = result.customerProfileResult?.profile;
  const travelers = (profile?.travelers as TravelerInfo[]) ?? [];

  // Extract structured profile data
  const p = profile as Record<string, unknown> | undefined;
  const travelPeriod = p?.travel_period as Record<string, unknown> | undefined;
  const food = p?.food as Record<string, unknown> | undefined;
  const healthAndPhysical = p?.health_and_physical as
    | Record<string, unknown>
    | undefined;
  const languages = p?.languages as Record<string, string[]> | undefined;
  const travelPreferences = p?.travel_preferences as
    | Record<string, unknown>
    | undefined;
  const hotelPreferences = p?.hotel_preferences as
    | Record<string, unknown>
    | undefined;
  const budget = p?.budget as Record<string, unknown> | undefined;
  const agency = p?.agency as Record<string, unknown> | undefined;
  const agents = (p?.agents as Array<Record<string, string>>) ?? [];
  const extractedNotes: ExtractedNote[] = Array.isArray(p?.extracted_notes)
    ? (p.extracted_notes as ExtractedNote[])
    : [];
  const specialRequests =
    (p?.special_requests as Array<Record<string, string>>) ?? [];
  const visitingCities = (p?.visiting_cities as string[]) ?? [];

  // Data conflicts
  const dataConflicts: DataConflict[] = Array.isArray(p?.data_conflicts)
    ? (p.data_conflicts as DataConflict[])
    : [];

  // Flight info
  const flightInfo = p?.flight_info as
    | { arrival?: FlightDetail; departure?: FlightDetail }
    | undefined;
  const hasFlightInfo =
    flightInfo &&
    (str(flightInfo.arrival?.flight_code) ||
      str(flightInfo.departure?.flight_code));

  // Traveler relationships
  const travelerRelationships: TravelerRelationship[] = Array.isArray(
    p?.traveler_relationships,
  )
    ? (p.traveler_relationships as TravelerRelationship[])
    : [];

  const [allTopicsOpen, setAllTopicsOpen] = useState(false);

  // Food items extraction
  const foodAllergyArr = (
    Array.isArray(food?.allergies) ? food.allergies : []
  ) as Array<Record<string, unknown>>;
  const foodRestrictionArr = (
    Array.isArray(food?.confirmed_restrictions)
      ? food.confirmed_restrictions
      : []
  ) as Array<Record<string, unknown>>;
  const foodRequestArr = (
    Array.isArray(food?.requests) ? food.requests : []
  ) as Array<Record<string, unknown>>;
  const foodAvoidanceArr = (
    Array.isArray(food?.avoidances) ? food.avoidances : []
  ) as Array<Record<string, unknown>>;

  // Health
  const medicalNotesArr =
    (healthAndPhysical?.medical_notes as Array<Record<string, unknown>>) ?? [];

  // Languages
  const spokenLangs = languages?.spoken ?? [];
  const guideLangs = languages?.guide_languages ?? [];

  // Travel preferences
  const interests = (travelPreferences?.interests as string[]) ?? [];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* U6: 再実行後の「解消/新規/継続」確定値サマリ帯（Executive Summary よりさらに上）。
          確定値（履歴 API）のみ・前回比較が無い run では自動で非表示。完了済み結果のときだけ出す。 */}
      {result.completedAt ? (
        <RunDiffSummaryBadges
          projectId={result.projectId}
          runId={result.runId}
          language={result.language}
          onJump={onJumpToHistory}
        />
      ) : null}

      {/* Executive Summary (afterAll) */}
      {showExecutiveSummary && basicRequestsSummary ? (
        <div
          className={`rounded-lg border p-5 ${
            basicRequestsSummary.overallScore != null &&
            basicRequestsSummary.overallScore >= 80
              ? "bg-indigo-50/50 border-indigo-200"
              : basicRequestsSummary.overallScore != null &&
                  basicRequestsSummary.overallScore >= 50
                ? "bg-indigo-50/50 border-indigo-300"
                : "bg-indigo-50/50 border-indigo-400"
          }`}
        >
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-indigo-800">
                Executive Summary
              </h3>
              <SourceBadge source="ai" />
            </div>
            {basicRequestsSummary.overallScore != null && (
              <span
                className={`text-lg font-bold tabular-nums ${
                  basicRequestsSummary.overallScore >= 80
                    ? "text-green-600"
                    : basicRequestsSummary.overallScore >= 50
                      ? "text-yellow-600"
                      : "text-red-600"
                }`}
              >
                {basicRequestsSummary.overallScore}
                <span className="text-xs font-normal text-gray-400">/100</span>
              </span>
            )}
          </div>
          <SummaryWithCitations
            summary={
              basicRequestsSummary.summary ??
              basicRequestsSummaryData?.summary ??
              "—"
            }
            summarySections={basicRequestsSummarySections}
            findings={basicRequestsFindings}
            onCitationHover={handleCitationHover}
            onCitationLeave={handleCitationLeave}
            onCitationClick={handleCitationClick}
          />
          {hoveredPreviewContent ? (
            <CitationPopover
              anchorRect={hoveredPreviewContent.anchorRect}
              badgeLabel={hoveredPreviewContent.badgeLabel}
              badgeTone={hoveredPreviewContent.badgeTone}
              title={hoveredPreviewContent.title}
              meta={hoveredPreviewContent.meta}
              body={hoveredPreviewContent.body}
              onMouseEnter={() => {
                clearCitationCloseTimer();
              }}
              onMouseLeave={() => {
                scheduleCitationPreviewClose();
              }}
              onClick={handlePreviewClick}
            />
          ) : null}
          {/* Findings (collapsible) */}
          {basicRequestsFindings.length > 0 && (
            <details ref={summaryFindingsDetailsRef} className="mt-3">
              <summary className="text-xs font-medium text-indigo-600 cursor-pointer hover:text-indigo-800 transition-colors">
                Findings ({basicRequestsFindings.length})
              </summary>
              <div className="mt-2 space-y-2">
                {basicRequestsFindings.map((f, i) => {
                  const primarySourceCheckId = primarySourceCheckIdOf(f);
                  const evidenceEntries = primarySourceCheckId
                    ? (evidenceEntriesByCheckId.get(primarySourceCheckId) ?? [])
                    : [];
                  const validEvidenceRefs = sanitizeEvidenceRefs(
                    f.evidence_refs,
                    evidenceEntries.length,
                  );

                  return (
                    <div
                      id={`executive-summary-finding-${i}`}
                      key={`brs-finding-${i}`}
                      className={`rounded-md border px-3 py-2 text-sm ${
                        f.severity === "critical"
                          ? "border-red-200 bg-red-50/50"
                          : f.severity === "warning"
                            ? "border-yellow-200 bg-yellow-50/50"
                            : "border-gray-200 bg-gray-50/50"
                      } ${
                        highlightedSummaryFindingIndex === i
                          ? "ring-2 ring-amber-300 border-amber-300 shadow-sm"
                          : ""
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                            f.severity === "critical"
                              ? "bg-red-100 text-red-700"
                              : f.severity === "warning"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {String(f.severity ?? "info")}
                        </span>
                        {String(f.check_point ?? "") && (
                          <span className="text-xs text-gray-500">
                            {String(f.check_point)}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-700">
                        <MarkdownText mode="block">
                          {String(f.finding ?? "")}
                        </MarkdownText>
                      </div>
                      {basicRequestsSummary?.checkId &&
                        result.projectId != null && (
                          <ActionDecisionButtons
                            finding={f as unknown as Record<string, unknown>}
                            feedbackCtx={{
                              projectId: String(result.projectId),
                              checkId: basicRequestsSummary.checkId,
                            }}
                          />
                        )}
                      {primarySourceCheckId && validEvidenceRefs.length > 0 ? (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                            Evidence Refs
                          </span>
                          {validEvidenceRefs.map((evidenceRef, refIndex) => (
                            <button
                              key={`evidence-ref-${i}-${evidenceRef}`}
                              type="button"
                              onMouseEnter={(event) => {
                                const rect = (
                                  event.currentTarget as HTMLElement
                                ).getBoundingClientRect();
                                openEvidencePreview(
                                  primarySourceCheckId,
                                  evidenceRef,
                                  i,
                                  rect,
                                );
                              }}
                              onMouseLeave={handleCitationLeave}
                              onClick={() => {
                                handleJumpToEvidence(
                                  primarySourceCheckId,
                                  evidenceRef,
                                  i,
                                );
                              }}
                              className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50"
                              aria-label={`Jump to evidence ${refIndex + 1}`}
                            >
                              [{String.fromCharCode(97 + refIndex)}]
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {String(f.recommendation ?? "") && (
                        <div className="mt-1.5">
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                            Recommendation
                          </div>
                          <div className="text-xs text-gray-500">
                            <MarkdownText mode="block">
                              {String(f.recommendation ?? "")}
                            </MarkdownText>
                          </div>
                        </div>
                      )}
                      {Array.isArray(f.source_check_ids) &&
                      f.source_check_ids.length > 0 ? (
                        <div className="mt-2">
                          <div className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">
                            Source Checks
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {f.source_check_ids.map((sourceCheckId) => {
                              const target =
                                resolveCheckReference(sourceCheckId);
                              return (
                                <button
                                  key={sourceCheckId}
                                  type="button"
                                  onMouseEnter={(event) => {
                                    if (!target.available) return;
                                    const rect = (
                                      event.currentTarget as HTMLElement
                                    ).getBoundingClientRect();
                                    openCheckPreview(target.id, i, rect);
                                  }}
                                  onMouseLeave={handleCitationLeave}
                                  onClick={() => {
                                    if (!target.available) return;
                                    handleJumpToCheck(sourceCheckId, {
                                      findingIndex: i,
                                    });
                                  }}
                                  disabled={!target.available}
                                  className={`rounded-full border px-2 py-1 text-xs font-medium transition-colors ${
                                    target.available
                                      ? "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-100"
                                      : "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                                  }`}
                                >
                                  {target.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                      {f.evidence != null && f.evidence !== "" && (
                        <details className="my-1.5">
                          <summary className="text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 transition-colors">
                            Evidence
                          </summary>
                          <div className="mt-1 text-xs text-gray-500 break-words">
                            <EvidenceValue
                              value={f.evidence}
                              context={{
                                evidencePathOffsets: new Map<string, number>(),
                                labelResolver,
                                onProjectDataReferenceClick,
                              }}
                            />
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      ) : null}

      {/* Data Conflicts Alert */}
      {dataConflicts.length > 0 && (
        <CollapsibleBlock
          open={dataConflictsOpen}
          onToggle={() => setDataConflictsOpen((v) => !v)}
          title={
            <>
              <AlertTriangle size={16} className="text-red-600 shrink-0" />
              <span className="text-red-700">Data Conflicts</span>
            </>
          }
          badge={
            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              {dataConflicts.length}
            </span>
          }
        >
          <div className="space-y-2">
            {dataConflicts.map((c, i) => (
              <div
                key={`conflict-${i}`}
                className={`rounded-md border p-3 text-sm ${
                  c.severity === "error"
                    ? "border-red-200 bg-white"
                    : "border-yellow-200 bg-yellow-50/50"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                      c.severity === "error"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {c.severity}
                  </span>
                  {c.requires_confirmation && (
                    <span className="text-xs text-red-500 font-medium">
                      Requires confirmation
                    </span>
                  )}
                </div>
                <p className="text-gray-800 font-medium">{c.description}</p>
                {(c.field_a || c.field_b) && (
                  <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                    {c.field_a && <div>Field A: {c.field_a}</div>}
                    {c.field_b && <div>Field B: {c.field_b}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CollapsibleBlock>
      )}

      {/* Critical Checks (collapsible when > 3) */}
      {criticalChecks.length > 0 && (
        <CollapsibleBlock
          open={criticalOpen}
          onToggle={() => setCriticalOpen((v) => !v)}
          title={
            <>
              <AlertCircle size={16} className="text-red-600 shrink-0" />
              <span className="text-red-600">Critical Checks</span>
            </>
          }
          badge={
            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              {criticalChecks.length}
            </span>
          }
        >
          <div className="grid gap-3">
            {(showAllCritical
              ? criticalChecks
              : criticalChecks.slice(0, CRITICAL_COLLAPSE_THRESHOLD)
            ).map((check, i) => (
              <div
                key={`critical-${check.title}-${i}`}
                className={`p-4 rounded-lg border flex gap-3 ${
                  check.severity === "high"
                    ? "bg-red-50 border-red-200"
                    : "bg-yellow-50 border-yellow-200"
                }`}
              >
                <div
                  className={`mt-0.5 ${check.severity === "high" ? "text-red-600" : "text-yellow-600"}`}
                >
                  <AlertCircle size={18} />
                </div>
                <div>
                  <div
                    className={`text-sm font-bold mb-1 flex items-center gap-2 ${
                      check.severity === "high"
                        ? "text-red-800"
                        : "text-yellow-800"
                    }`}
                  >
                    {check.title}
                    <SourceBadge source={check.source} />
                    <span
                      className={`text-xs font-medium ${getImportanceColor(check.importance)}`}
                    >
                      {check.importance}
                    </span>
                  </div>
                  <div
                    className={`text-sm ${
                      check.severity === "high"
                        ? "text-red-700"
                        : "text-yellow-700"
                    }`}
                  >
                    <MarkdownText mode="block">
                      {check.description}
                    </MarkdownText>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {criticalChecks.length > CRITICAL_COLLAPSE_THRESHOLD && (
            <button
              type="button"
              onClick={() => setShowAllCritical(!showAllCritical)}
              className="mt-3 text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
            >
              {showAllCritical
                ? "Show less"
                : `Show ${criticalChecks.length - CRITICAL_COLLAPSE_THRESHOLD} more`}
            </button>
          )}
        </CollapsibleBlock>
      )}

      {/* Participants & Profile Grid */}
      {profile && (
        <CollapsibleBlock
          open={profileOpen}
          onToggle={() => setProfileOpen((v) => !v)}
          title={<span className="text-gray-700">Customer Profile</span>}
          badge={
            travelers.length > 0 ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {travelers.length}{" "}
                {travelers.length === 1 ? "traveler" : "travelers"}
              </span>
            ) : undefined
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Participants */}
            <div className="lg:col-span-1 space-y-3">
              <h3 className="text-sm font-bold text-gray-700">Participants</h3>
              <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                {travelers.map((t, i) => (
                  <div
                    key={`traveler-${t.name ?? i}`}
                    className="p-3 space-y-1"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-medium shrink-0">
                        {(t.name ?? "?").charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 line-clamp-1 flex items-center gap-2">
                          {t.honorific && (
                            <span className="text-gray-400 text-xs">
                              {t.honorific}
                            </span>
                          )}
                          {t.name}
                          {t.is_primary && (
                            <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 line-clamp-1">
                          {[
                            t.role,
                            t.gender,
                            t.age_group,
                            t.age ? `${t.age}y` : "",
                            t.birthday,
                            t.nationality,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                    </div>
                    {/* Extended details */}
                    {(t.job ||
                      t.residence_country ||
                      t.smoking_status ||
                      t.japan_visit_times != null ||
                      t.religion ||
                      t.religious_and_cultural_note) && (
                      <div className="pl-11 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                        {t.job && <span>Job: {t.job}</span>}
                        {t.residence_country && (
                          <span>
                            Residence: {t.residence_country}
                            {t.residence_region
                              ? ` (${t.residence_region})`
                              : ""}
                          </span>
                        )}
                        {t.smoking_status && (
                          <span>Smoking: {t.smoking_status}</span>
                        )}
                        {t.japan_visit_times != null && (
                          <span>
                            Japan visits: {t.japan_visit_times}
                            {t.japan_visit_history
                              ? ` (${t.japan_visit_history})`
                              : ""}
                          </span>
                        )}
                        {t.religion && <span>Religion: {t.religion}</span>}
                        {t.religious_and_cultural_note && (
                          <span>Note: {t.religious_and_cultural_note}</span>
                        )}
                      </div>
                    )}
                    {t.notes && (
                      <div className="pl-11 text-xs text-gray-500 italic">
                        {t.notes}
                      </div>
                    )}
                  </div>
                ))}
                {travelers.length === 0 && (
                  <div className="p-3 text-sm text-gray-400">
                    No participant data available
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Profile Detail Cards */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-sm font-bold text-gray-700">
                Customer Profile
              </h3>

              {/* 1. Trip Overview */}
              <ProfileCard source="ai" title="Trip Overview">
                <div className="space-y-2">
                  <InfoRow
                    label="Period"
                    value={
                      travelPeriod
                        ? [
                            str(travelPeriod.start_date),
                            str(travelPeriod.end_date),
                          ]
                            .filter(Boolean)
                            .join(" ~ ") +
                          (travelPeriod.duration_days
                            ? ` (${travelPeriod.duration_days} days)`
                            : "")
                        : ""
                    }
                  />
                  <InfoRow label="Type" value={str(p?.travel_type)} />
                  <InfoRow label="Tour Code" value={str(p?.tour_code)} />
                  <InfoRow
                    label="Agency"
                    value={
                      agency && str(agency.name)
                        ? str(agency.name) +
                          (agency.tier ? ` (Tier ${agency.tier})` : "")
                        : ""
                    }
                  />
                  <InfoRow
                    label="Agents"
                    value={
                      agents.length > 0
                        ? agents
                            .map(
                              (a) =>
                                str(a.name) + (a.email ? ` <${a.email}>` : ""),
                            )
                            .join(", ")
                        : ""
                    }
                  />
                </div>
              </ProfileCard>

              {/* 1b. Flight Info */}
              {hasFlightInfo && (
                <ProfileCard source="ai" title="Flight Info">
                  <div className="space-y-3">
                    {flightInfo.arrival?.flight_code && (
                      <FlightEntry
                        direction="arrival"
                        detail={flightInfo.arrival}
                        contactTopics={contactTopics}
                        labelResolver={labelResolver}
                      />
                    )}
                    {flightInfo.departure?.flight_code && (
                      <FlightEntry
                        direction="departure"
                        detail={flightInfo.departure}
                        contactTopics={contactTopics}
                        labelResolver={labelResolver}
                      />
                    )}
                  </div>
                </ProfileCard>
              )}

              {/* 2. Food & Dietary */}
              <ProfileCard source="ai" title="Food & Dietary">
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Allergies
                    </div>
                    <DetailedItemList
                      items={foodAllergyArr}
                      color="red"
                      labelKey="item"
                      contactTopics={contactTopics}
                      labelResolver={labelResolver}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Restrictions
                    </div>
                    <DetailedItemList
                      items={foodRestrictionArr}
                      color="orange"
                      labelKey="item"
                      contactTopics={contactTopics}
                      labelResolver={labelResolver}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Requests
                    </div>
                    <DetailedItemList
                      items={foodRequestArr}
                      color="green"
                      labelKey="request"
                      contactTopics={contactTopics}
                      labelResolver={labelResolver}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Avoidances
                    </div>
                    <DetailedItemList
                      items={foodAvoidanceArr}
                      color="yellow"
                      labelKey="item"
                      contactTopics={contactTopics}
                      labelResolver={labelResolver}
                      renderExtra={(item) =>
                        str(item.reason) || str(item.severity) ? (
                          <div className="flex gap-2 text-xs text-gray-500 mt-0.5">
                            {str(item.reason) && (
                              <span>Reason: {str(item.reason)}</span>
                            )}
                            {str(item.severity) && (
                              <span>Severity: {str(item.severity)}</span>
                            )}
                          </div>
                        ) : null
                      }
                    />
                  </div>
                  {str(food?.notes) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {str(food?.notes)}
                    </p>
                  )}
                </div>
              </ProfileCard>

              {/* 3. Health & Physical */}
              <ProfileCard source="ai" title="Health & Physical">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Medical Notes
                    </div>
                    {medicalNotesArr.length > 0 ? (
                      <div className="space-y-2">
                        {medicalNotesArr.map((n, i) => (
                          <div key={i} className="text-sm text-gray-700">
                            <div className="flex items-center gap-1.5">
                              <span>• {str(n.note)}</span>
                              {str(n.person) && (
                                <span className="text-gray-400 text-xs">
                                  ({str(n.person)})
                                </span>
                              )}
                            </div>
                            <SourceEvidence
                              sourceField={str(n.source_field) || undefined}
                              sourceNote={str(n.source_note) || undefined}
                              contactTopics={contactTopics}
                              labelResolver={labelResolver}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </div>
                  <InfoRow
                    label="Mobility"
                    value={str(healthAndPhysical?.mobility_concerns)}
                  />
                  <InfoRow
                    label="Special Requirements"
                    value={str(healthAndPhysical?.special_requirements)}
                  />
                </div>
              </ProfileCard>

              {/* 4. Languages */}
              <ProfileCard source="ai" title="Languages">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Spoken
                    </div>
                    {spokenLangs.length > 0 ? (
                      <BadgeList items={spokenLangs} color="blue" />
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Guide Languages
                    </div>
                    {guideLangs.length > 0 ? (
                      <BadgeList items={guideLangs} color="gray" />
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </div>
                </div>
              </ProfileCard>

              {/* 5. Travel Preferences */}
              <ProfileCard source="ai" title="Travel Preferences">
                <div className="space-y-2">
                  <InfoRow
                    label="Purpose"
                    value={str(travelPreferences?.trip_purpose)}
                  />
                  <InfoRow
                    label="Style"
                    value={str(travelPreferences?.style)}
                  />
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Interests
                    </div>
                    {interests.length > 0 ? (
                      <BadgeList items={interests} color="blue" />
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </div>
                  <InfoRow
                    label="Anniversary"
                    value={str(travelPreferences?.anniversary_note)}
                  />
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Activity Preferences
                    </div>
                    {Array.isArray(travelPreferences?.activity_preferences) &&
                    (travelPreferences.activity_preferences as string[])
                      .length > 0 ? (
                      <BadgeList
                        items={
                          travelPreferences.activity_preferences as string[]
                        }
                        color="green"
                      />
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </div>
                  {str(travelPreferences?.other) && (
                    <InfoRow
                      label="Other"
                      value={str(travelPreferences?.other)}
                    />
                  )}
                </div>
              </ProfileCard>

              {/* 5b. Traveler Relationships */}
              {travelerRelationships.length > 0 && (
                <ProfileCard source="ai" title="Traveler Relationships">
                  <div className="space-y-2.5">
                    {travelerRelationships.map((rel, i) => (
                      <div key={`rel-${i}`}>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-800">
                            {str(rel.person_a)}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                            {str(rel.relationship)}
                          </span>
                          <span className="font-medium text-gray-800">
                            {str(rel.person_b)}
                          </span>
                          {rel.confidence && (
                            <span
                              className={`text-xs ${rel.confidence === "confirmed" ? "text-green-500" : "text-gray-400"}`}
                            >
                              ({rel.confidence})
                            </span>
                          )}
                        </div>
                        {rel.evidence && (
                          <p className="text-xs text-gray-400 mt-0.5 break-words whitespace-pre-wrap italic ml-1">
                            {rel.evidence}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </ProfileCard>
              )}

              {/* 6. Accommodation & Logistics */}
              <ProfileCard source="ai" title="Accommodation & Logistics">
                <div className="space-y-2">
                  <InfoRow
                    label="Hotel Rank"
                    value={str(
                      (hotelPreferences as Record<string, unknown>)?.rank,
                    )}
                  />
                  <InfoRow
                    label="Room Type"
                    value={str(
                      (hotelPreferences as Record<string, unknown>)?.room_type,
                    )}
                  />
                  <InfoRow
                    label="Other"
                    value={str(
                      (hotelPreferences as Record<string, unknown>)
                        ?.other_requirements,
                    )}
                  />
                  <InfoRow
                    label="Budget"
                    value={str(
                      (budget as Record<string, unknown>)?.budget_note,
                    )}
                  />
                  <InfoRow
                    label="Baggage"
                    value={str(
                      (budget as Record<string, unknown>)?.baggage_note,
                    )}
                  />
                  <InfoRow
                    label="Transfer Support"
                    value={str(p?.transfer_support)}
                  />
                  <div>
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      Visiting Cities
                    </div>
                    {visitingCities.length > 0 ? (
                      <BadgeList items={visitingCities} color="blue" />
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </div>
                </div>
              </ProfileCard>

              {/* 7. Extracted Notes */}
              <ProfileCard source="ai" title="Extracted Notes">
                {result.contactTopicsResult && (
                  <div className="mb-3">
                    <ContactCompactionSummaryRow
                      contactTopicsResult={result.contactTopicsResult}
                      onOpenAll={() => setAllTopicsOpen(true)}
                    />
                  </div>
                )}
                <ExtractedNotesList
                  notes={extractedNotes}
                  contactTopics={contactTopics}
                  labelResolver={labelResolver}
                />
                {result.contactTopicsResult && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setAllTopicsOpen(true)}
                      className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 transition-colors font-medium"
                    >
                      View all topics ({contactTopics.length})
                    </button>
                  </div>
                )}
              </ProfileCard>
              {allTopicsOpen && result.contactTopicsResult && (
                <AllContactTopicsDialog
                  contactTopicsResult={result.contactTopicsResult}
                  onClose={() => setAllTopicsOpen(false)}
                  labelResolver={labelResolver}
                />
              )}

              {/* 8. Special Requests */}
              <ProfileCard source="ai" title="Special Requests">
                {specialRequests.length > 0 ? (
                  <SpecialRequestsList
                    requests={specialRequests}
                    contactTopics={contactTopics}
                    labelResolver={labelResolver}
                  />
                ) : (
                  <span className="text-sm text-gray-300">—</span>
                )}
              </ProfileCard>

              {/* Additional Notes */}
              <ProfileCard source="ai" title="Additional Notes">
                <p
                  className={`text-sm leading-relaxed ${str(p?.additional_notes) ? "text-gray-700" : "text-gray-300"}`}
                >
                  {str(p?.additional_notes) || "—"}
                </p>
              </ProfileCard>
            </div>
          </div>
        </CollapsibleBlock>
      )}

      {/* Customer Profile Empty State */}
      {!profile && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
          Customer profile extraction is not enabled or did not produce results.
        </div>
      )}

      {/* Check Results */}
      {checkItems.length > 0 && (
        <div ref={checkResultsSectionRef}>
          <CollapsibleBlock
            open={checkResultsOpen}
            onToggle={() => setCheckResultsOpen((v) => !v)}
            title={<span className="text-gray-700">Check Results</span>}
            badge={
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {checkActiveCount}
              </span>
            }
          >
            <div className="flex flex-col lg:flex-row gap-6 lg:h-[500px]">
              <div className="flex-1 min-h-0">
                <div
                  ref={checkResultsListRef}
                  className="flex flex-col gap-3 overflow-auto lg:h-full pr-1"
                >
                  {/* Error items */}
                  {checkErrorItems.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-700">
                        Evaluation Errors
                      </div>
                      <div className="space-y-2">
                        {checkErrorItems.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            id={`check-card-${item.id}`}
                            onClick={() => handleManualCheckSelect(item.id)}
                            className={`group relative flex items-start gap-3 rounded-md border p-3 text-left transition-all cursor-pointer w-full ${
                              highlightCheckId === item.id
                                ? "border-amber-300 bg-white ring-2 ring-amber-300 shadow-md"
                                : "border-red-200 bg-white hover:border-red-300 hover:shadow-sm"
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-bold text-sm text-red-800 group-hover:text-red-900 transition-colors">
                                  {item.title}
                                </h4>
                              </div>
                              <p className="text-xs text-red-700 line-clamp-2">
                                {item.detail || "Evaluation failed."}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Filter bar */}
                  <StatusFilterButtons
                    statusFilter={checkStatusFilter}
                    setStatusFilter={setCheckStatusFilter}
                    statusOptions={checkStatusOptions}
                    activeCount={checkActiveCount}
                    dismissedCount={dismissedCount}
                    hideDismissed={hideCheckDismissed}
                    setHideDismissed={setHideCheckDismissed}
                  />

                  {/* Active items */}
                  {checkActiveItems.map((item) => (
                    <CheckItemCard
                      key={item.id}
                      id={item.id}
                      title={item.title}
                      detail={item.detail}
                      severity={item.severity}
                      status={item.status}
                      importance={item.importance}
                      score={item.score}
                      rawAIScore={item.rawAIScore}
                      scoreAdjusted={item.scoreAdjusted}
                      source={item.source}
                      isSelected={selectedCheckId === item.id}
                      isHighlighted={highlightCheckId === item.id}
                      isDismissed={false}
                      onSelect={() => handleManualCheckSelect(item.id)}
                      onDismiss={() => dismiss(item.id)}
                      onRestore={() => restore(item.id)}
                    />
                  ))}

                  {/* Dismissed items */}
                  {!hideCheckDismissed && checkDismissedItems.length > 0 && (
                    <div className="opacity-40 space-y-3 mt-4 pt-4 border-t border-dashed border-zinc-300">
                      {checkDismissedItems.map((item) => (
                        <CheckItemCard
                          key={item.id}
                          id={item.id}
                          title={item.title}
                          detail={item.detail}
                          severity={item.severity}
                          status={item.status}
                          importance={item.importance}
                          score={item.score}
                          rawAIScore={item.rawAIScore}
                          scoreAdjusted={item.scoreAdjusted}
                          source={item.source}
                          isSelected={selectedCheckId === item.id}
                          isHighlighted={highlightCheckId === item.id}
                          isDismissed={true}
                          onSelect={() => handleManualCheckSelect(item.id)}
                          onDismiss={() => dismiss(item.id)}
                          onRestore={() => restore(item.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Detail panel */}
              <div className="lg:w-[420px] xl:w-[540px]">
                {selectedCheckItem ? (
                  <CheckItemDetailPanel
                    title={selectedCheckItem.title}
                    detail={selectedCheckItem.detail}
                    status={selectedCheckItem.status}
                    importance={selectedCheckItem.importance}
                    score={selectedCheckItem.score}
                    rawAIScore={selectedCheckItem.rawAIScore}
                    scoreAdjusted={selectedCheckItem.scoreAdjusted}
                    source={selectedCheckItem.source}
                    rawResult={selectedCheckItem.rawResult}
                    projectData={result.projectData}
                    contactTopics={contactTopics}
                    isDismissed={isDismissed(selectedCheckItem.id)}
                    onDismiss={() => dismiss(selectedCheckItem.id)}
                    onRestore={() => restore(selectedCheckItem.id)}
                    onClose={() => setSelectedCheckId(null)}
                    headerAction={
                      summaryReturnTarget?.checkId === selectedCheckItem.id ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (onReturnToSummary) {
                              onReturnToSummary(
                                summaryReturnTarget.findingIndex,
                              );
                            } else {
                              setSummaryReturnTarget(null);
                              setFocusedSummaryFindingIndex(
                                summaryReturnTarget.findingIndex,
                              );
                              onJumpToTab?.("basic");
                            }
                          }}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          ← Executive Summary
                        </button>
                      ) : null
                    }
                    evidenceIdPrefix={`check-evidence-${selectedCheckItem.id}`}
                    focusedEvidenceIndex={
                      pendingEvidenceTarget?.checkId === selectedCheckItem.id
                        ? pendingEvidenceTarget.evidenceIndex
                        : null
                    }
                    highlightedEvidenceIndex={
                      highlightEvidenceTarget?.checkId === selectedCheckItem.id
                        ? highlightEvidenceTarget.evidenceIndex
                        : null
                    }
                    onProjectDataReferenceClick={onProjectDataReferenceClick}
                    onFocusedEvidenceHandled={() =>
                      setPendingEvidenceTarget((current) =>
                        current?.checkId === selectedCheckItem.id
                          ? null
                          : current,
                      )
                    }
                    feedbackContext={{
                      runId: (result as { runId?: string }).runId,
                      projectId:
                        result.projectId != null
                          ? String(result.projectId)
                          : undefined,
                      checkId: selectedCheckItem.id,
                    }}
                  />
                ) : (
                  <CheckItemDetailEmpty />
                )}
              </div>
            </div>
          </CollapsibleBlock>
        </div>
      )}
    </div>
  );
}
